import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiBookOpen, FiMessageSquare, FiUserCheck, FiBarChart2, FiCopy, FiCheck } from "react-icons/fi";
import SupportSandbox from "./support/SupportSandbox";
import { PUBLIC_API_BASE, codeSamples } from "../chatbot/chat";
import { supportSamples } from "../chatbot/support";

const STEPS = [
  [FiBookOpen, "Teach it", "Add your FAQs, policies and product facts: type them, upload PDF or Word files, or import your help pages."],
  [FiMessageSquare, "It answers customers", "By text or voice, in their language, from your facts only. It says when it does not know, and shows where the answer came from."],
  [FiUserCheck, "It hands over to a person", "When a customer asks for a human, is upset, or needs something only your team can do, it takes their contact details and makes a ticket."],
  [FiBarChart2, "You see what is missing", "Customers rate the answers. You see the questions it could not answer, and add them to its knowledge."],
];

function Code({ text }) {
  const [done, setDone] = useState(false);
  return (
    <div className="relative">
      <pre className="rounded-xl bg-black/40 border border-navy-border p-4 pr-24 overflow-x-auto text-[12.5px] leading-relaxed text-gray-200 whitespace-pre" tabIndex={0}><code>{text}</code></pre>
      <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* select by hand */ } }}
        className="absolute top-2.5 right-2.5 px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-xs inline-flex items-center gap-1.5">{done ? <><FiCheck /> Copied</> : <><FiCopy /> Copy</>}</button>
    </div>
  );
}

// AI Customer Support Assistant: a try-it box (the visitor teaches an assistant their own FAQ and talks to it), how it works, and the API for support tickets.
export default function SupportAssistantTool() {
  const [tab, setTab] = useState("flow");
  const samples = useMemo(() => ({ flow: supportSamples(PUBLIC_API_BASE).flow, embed: codeSamples(PUBLIC_API_BASE, "YOUR_API_KEY", "YOUR_WIDGET_KEY").embed }), []);
  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Customer Support Assistant</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-10">
        An assistant that answers your customers' questions around the clock, from your own FAQs and documents - and hands the conversation to your team, with the customer's details, when a person is needed.
        Teach it a few facts below and ask it questions like a customer would.
      </p>

      <SupportSandbox />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14 mb-14">
        {STEPS.map(([Icon, title, text], i) => (
          <div key={title} className="rounded-2xl bg-white/5 border border-navy-border p-5">
            <div className="flex items-center gap-3 mb-2"><span className="w-9 h-9 rounded-xl bg-brand-gradient flex items-center justify-center"><Icon /></span><span className="text-xs text-gray-500">Step {i + 1}</span></div>
            <div className="font-semibold mb-1">{title}</div>
            <p className="text-sm text-gray-400">{text}</p>
          </div>
        ))}
      </div>

      <h3 className="text-xl md:text-2xl font-bold mb-2">Plug it into your website or app</h3>
      <p className="text-gray-400 mb-5 max-w-3xl">A chat window for your website with one line of code, or the API for your own app, help desk or WhatsApp bridge. Text, voice, tickets and ratings are all in the same API.</p>
      <div role="tablist" aria-label="Examples" className="flex flex-wrap gap-2 mb-3">
        {[["flow", "Tickets and ratings (API)"], ["embed", "Website chat window"]].map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key} type="button" onClick={() => setTab(key)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border ${tab === key ? "bg-accent-light text-navy border-accent-light" : "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light"}`}>{label}</button>
        ))}
      </div>
      <Code text={samples[tab]} />
      <p className="text-xs text-gray-500 mt-2">The chat and voice endpoints are described on the <Link className="underline" to="/ai-chatbots-and-virtual-assistants?tool=custom-ai-chatbot-development">Custom AI Chatbot</Link> page.</p>

      <div className="mt-12 rounded-2xl bg-white/5 border border-navy-border p-6 md:p-8 text-center">
        <h3 className="text-lg font-bold mb-2">Want a support assistant for your business?</h3>
        <p className="text-sm text-gray-400 max-w-2xl mx-auto mb-5">Send us your FAQs or website address. We train the assistant on them, add the chat window to your site, and give you the ticket inbox and the API.</p>
        <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-gradient font-semibold shadow-lg shadow-accent/20">Get your support assistant <FiArrowRight /></Link>
      </div>
    </div>
  );
}
